import { createListenerMiddleware, type UnknownAction } from '@reduxjs/toolkit';
import { reportsApi } from '../reportsApi';
import type { ReportsBackendGateway } from '../reportsGateway';
import {
  createBackendGatewayError,
  isBackendGatewayError,
  toAppError,
} from '../../../utils/backendGatewayError';
import { notificationAdded } from '../../notifications/notificationSlice';
import type { AppError } from '../../../utils/firebaseErrors';
import { clearReportReviewDeletionConsequences } from '../reportReadFamilyPolicy';
import { getFaceScreeningMediaIdentity } from '../reportPhotoIdentity';
import {
  getReportReviewOperationKey,
  type ReportReviewActionScope,
  type ReportReviewWorkflowState,
  reportPresenceLost,
  reportRejectionFinalizationRequested,
  reportReviewFaceScreeningFalsePositiveRequested,
  reportReviewFlaggedMediaRemovalRequested,
  reportReviewLabelsChangeRequested,
  reportReviewOperationAttempted,
  reportReviewOperationFailed,
  reportReviewOperationSucceeded,
  reportReviewSpeciesChangeRequested,
  reportReviewStatusDecisionRequested,
  selectReportReviewPendingOperations,
} from './reportReviewWorkflowSlice';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 500;

type ReportReviewWorkflowDependencies = {
  updateReportStatus: ReportsBackendGateway['updateReportStatus'];
  finalizeReportRejection: ReportsBackendGateway['finalizeReportRejection'];
  updateReportSpecies: ReportsBackendGateway['updateReportSpecies'];
  updateReportReviewMetadata: ReportsBackendGateway['updateReportReviewMetadata'];
  classifyReportFaceScreeningFalsePositive: ReportsBackendGateway['classifyReportFaceScreeningFalsePositive'];
  removeReportFaceScreeningMedia: ReportsBackendGateway['removeReportFaceScreeningMedia'];
  random?: () => number;
};

type AbortableDelay = {
  abort: () => void;
  done: Promise<boolean>;
};

const activeDelays = new Map<string, AbortableDelay>();

const abortActiveDelaysForReport = (reportId: string): void => {
  activeDelays.forEach((delay, key) => {
    if (!key.startsWith(`${reportId}:`)) {
      return;
    }

    delay.abort();
    activeDelays.delete(key);
  });
};

const isOperationCurrent = (
  state: unknown,
  reportId: string,
  scope: ReportReviewActionScope,
  targetId: string | undefined,
  operationId: string
): boolean => {
  const pendingOperations = selectReportReviewPendingOperations(
    state as { reportReviewWorkflow: ReportReviewWorkflowState }
  );
  const operation = pendingOperations[getReportReviewOperationKey(reportId, scope, targetId)];
  return operation?.operationId === operationId;
};

export const getReportReviewRetryDelayMs = (
  attempt: number,
  random: () => number = Math.random
): number => {
  const baseDelay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
  const jitter = Math.floor(baseDelay * 0.2 * random());
  return baseDelay + jitter;
};

const createAbortableDelay = (delayMs: number): AbortableDelay => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let resolveDelay: ((completed: boolean) => void) | null = null;

  const done = new Promise<boolean>((resolve) => {
    resolveDelay = resolve;
    timeoutId = setTimeout(() => {
      timeoutId = null;
      resolve(true);
    }, delayMs);
  });

  return {
    done,
    abort: () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      resolveDelay?.(false);
    },
  };
};

const normalizeReportReviewErrorCode = (code: string): string =>
  code.startsWith('functions/') ? code.slice('functions/'.length) : code;

const getErrorCode = (error: unknown): string | undefined => {
  if (isBackendGatewayError(error)) {
    return getErrorCode(error.original) ?? `${error.operation}/${error.reason}`;
  }

  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? normalizeReportReviewErrorCode(code) : undefined;
  }

  return undefined;
};

export const isRetryableReportReviewError = (error: unknown): boolean => {
  const code = getErrorCode(error);
  return (
    code === 'unavailable' ||
    code === 'network-request-failed' ||
    code === 'deadline-exceeded' ||
    code === 'internal'
  );
};

const toReportReviewAppError = (error: unknown): AppError => {
  if (isBackendGatewayError(error)) {
    return toAppError(error);
  }

  return toAppError(createBackendGatewayError('report', 'update-failed', error));
};

export const createReportReviewWorkflowListenerMiddleware = ({
  random = Math.random,
  updateReportStatus,
  finalizeReportRejection,
  updateReportSpecies,
  updateReportReviewMetadata,
  classifyReportFaceScreeningFalsePositive,
  removeReportFaceScreeningMedia,
}: ReportReviewWorkflowDependencies) => {
  const listenerMiddleware = createListenerMiddleware();

  const executeOperation = async ({
    listenerApi,
    reportId,
    scope,
    targetId,
    operationId,
    write,
  }: {
    listenerApi: { getState: () => unknown; dispatch: (action: UnknownAction) => unknown };
    reportId: string;
    scope: ReportReviewActionScope;
    targetId?: string;
    operationId: string;
    write: () => Promise<unknown>;
  }) => {
    type ReportRemovalWriteResult = { reportRejected?: boolean } | void;
    const key = getReportReviewOperationKey(reportId, scope, targetId);

    activeDelays.get(key)?.abort();
    activeDelays.delete(key);

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
      if (!isOperationCurrent(listenerApi.getState(), reportId, scope, targetId, operationId)) {
        return;
      }

      listenerApi.dispatch(
        reportReviewOperationAttempted({
          reportId,
          scope,
          targetId,
          operationId,
          attempt,
        })
      );

      try {
        const writeResult = (await write()) as ReportRemovalWriteResult;

        if (!isOperationCurrent(listenerApi.getState(), reportId, scope, targetId, operationId)) {
          return;
        }

        const reportDeleted = scope === 'removeMedia' && writeResult?.reportRejected === true;
        if (reportDeleted) {
          clearReportReviewDeletionConsequences(reportId);
        }

        listenerApi.dispatch(
          reportReviewOperationSucceeded({
            reportId,
            scope,
            targetId,
            operationId,
            reportDeleted,
          })
        );
        listenerApi.dispatch(
          reportsApi.util.invalidateTags(['Reports', { type: 'Reports', id: reportId }])
        );
        activeDelays.delete(key);
        return;
      } catch (error) {
        if (!isOperationCurrent(listenerApi.getState(), reportId, scope, targetId, operationId)) {
          return;
        }

        if (!isRetryableReportReviewError(error) || attempt >= MAX_RETRIES) {
          const appError = toReportReviewAppError(error);
          listenerApi.dispatch(
            reportReviewOperationFailed({
              reportId,
              scope,
              targetId,
              operationId,
              error: appError,
            })
          );
          listenerApi.dispatch(
            notificationAdded({
              severity: 'error',
              message: appError.message,
              reportId,
            })
          );
          listenerApi.dispatch(
            reportsApi.util.invalidateTags(['Reports', { type: 'Reports', id: reportId }])
          );
          activeDelays.delete(key);
          return;
        }

        const delay = createAbortableDelay(getReportReviewRetryDelayMs(attempt, random));
        activeDelays.set(key, delay);
        const completed = await delay.done;
        activeDelays.delete(key);

        if (!completed) {
          return;
        }
      }
    }
  };

  listenerMiddleware.startListening({
    actionCreator: reportPresenceLost,
    effect: (action) => {
      abortActiveDelaysForReport(action.payload.reportId);
    },
  });

  listenerMiddleware.startListening({
    actionCreator: reportReviewStatusDecisionRequested,
    effect: async (action, listenerApi) => {
      const { reportId, decision, operationId } = action.payload;
      await executeOperation({
        listenerApi,
        reportId,
        scope: 'status',
        targetId: undefined,
        operationId,
        write: () => updateReportStatus(reportId, decision),
      });
    },
  });

  listenerMiddleware.startListening({
    actionCreator: reportRejectionFinalizationRequested,
    effect: async (action, listenerApi) => {
      const { reportId, operationId } = action.payload;
      await executeOperation({
        listenerApi,
        reportId,
        scope: 'finalizeRejection',
        targetId: undefined,
        operationId,
        write: () => finalizeReportRejection(reportId),
      });
    },
  });

  listenerMiddleware.startListening({
    actionCreator: reportReviewSpeciesChangeRequested,
    effect: async (action, listenerApi) => {
      const { reportId, species, operationId } = action.payload;
      await executeOperation({
        listenerApi,
        reportId,
        scope: 'species',
        targetId: undefined,
        operationId,
        write: () => updateReportSpecies(reportId, species),
      });
    },
  });

  listenerMiddleware.startListening({
    actionCreator: reportReviewLabelsChangeRequested,
    effect: async (action, listenerApi) => {
      const { reportId, labels, operationId } = action.payload;
      await executeOperation({
        listenerApi,
        reportId,
        scope: 'labels',
        targetId: undefined,
        operationId,
        write: () => updateReportReviewMetadata(reportId, { labels }),
      });
    },
  });

  listenerMiddleware.startListening({
    actionCreator: reportReviewFaceScreeningFalsePositiveRequested,
    effect: async (action, listenerApi) => {
      const { reportId, mediaStoragePath, mediaIdentity, operationId } = action.payload;
      const targetId = getFaceScreeningMediaIdentity({ mediaStoragePath, mediaIdentity });
      await executeOperation({
        listenerApi,
        reportId,
        scope: 'falsePositive',
        targetId,
        operationId,
        write: () =>
          classifyReportFaceScreeningFalsePositive(reportId, mediaStoragePath, mediaIdentity),
      });
    },
  });

  listenerMiddleware.startListening({
    actionCreator: reportReviewFlaggedMediaRemovalRequested,
    effect: async (action, listenerApi) => {
      const { reportId, mediaStoragePath, mediaIdentity, operationId } = action.payload;
      const targetId = getFaceScreeningMediaIdentity({ mediaStoragePath, mediaIdentity });
      await executeOperation({
        listenerApi,
        reportId,
        scope: 'removeMedia',
        targetId,
        operationId,
        write: () => removeReportFaceScreeningMedia(reportId, mediaStoragePath, mediaIdentity),
      });
    },
  });

  return listenerMiddleware;
};
